import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { publicApi } from '../utils/api';

// Industriile cu cea mai mare acoperire in index (banking 35, leasing 38, pharma 29).
const FEATURED_INDUSTRIES = ['banking', 'leasing', 'pharma'];

/**
 * IndexHighlights — dovada sociala pe baza de date reale, nu citate anonime.
 *
 * Afiseaza top 3 branduri din clasamentul public pentru industriile principale,
 * cu link catre clasamentul complet. Inlocuieste sectiunea de testimoniale
 * anonime: pentru un produs care vinde masurare, cea mai credibila dovada e
 * chiar datele live, verificabile cu un click.
 *
 * Daca API-ul nu raspunde, sectiunea nu se afiseaza deloc (nu are fallback
 * fictiv — mai bine lipsa decat inventat).
 */
export default function IndexHighlights() {
  const { t } = useTranslation();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      FEATURED_INDUSTRIES.map((slug) =>
        publicApi.leaderboard(slug, 3)
          .then((r) => ({
            slug,
            label: r.data?.industry_label || slug,
            brands: (r.data?.brands || []).slice(0, 3),
          }))
          .catch(() => null)
      )
    ).then((results) => {
      if (cancelled) return;
      setBoards(results.filter((b) => b && b.brands.length > 0));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  if (loading || boards.length === 0) return null;

  return (
    <section style={{ padding: 'clamp(48px, 8vw, 80px) clamp(16px, 5vw, 48px)', background: 'linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)' }}>
      <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 'clamp(32px, 5vw, 48px)' }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: '#8B6914', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 12, fontFamily: 'DM Mono, monospace' }}>
            {t('landing.index_highlights.eyebrow')}
          </p>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: '#0A1428', margin: 0 }}>
            {t('landing.index_highlights.title')}
          </h2>
          <p style={{ fontSize: 15, color: '#4B5563', maxWidth: 640, margin: '16px auto 0', lineHeight: 1.6, fontFamily: 'Inter, system-ui, sans-serif' }}>
            {t('landing.index_highlights.subtitle')}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'clamp(20px, 3vw, 28px)' }}>
          {boards.map((board) => (
            <div key={board.slug} style={{
              background: '#FFFFFF',
              padding: 'clamp(24px, 3vw, 32px)',
              borderRadius: 16,
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 16px rgba(13,27,42,0.04)',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <div style={{ fontSize: 11, color: '#8B6914', textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, marginBottom: 16, fontFamily: 'DM Mono, monospace' }}>
                {board.label}
              </div>

              <div style={{ flex: 1 }}>
                {board.brands.map((b) => (
                  <div key={b.rank} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 0',
                    borderBottom: '1px solid #F3F4F6',
                  }}>
                    <span style={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                      background: b.rank === 1 ? 'linear-gradient(135deg, #D4AF37 0%, #B8972E 100%)' : '#F3F4F6',
                      color: b.rank === 1 ? '#0A1428' : '#6B7280',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'Playfair Display, serif', fontSize: 14, fontWeight: 700,
                    }}>
                      {b.rank}
                    </span>
                    {b.slug ? (
                      <Link
                        to={`/branduri/${board.slug}/${b.slug}`}
                        style={{ flex: 1, textDecoration: 'none', color: '#0A1428', fontFamily: 'Cormorant Garamond, serif', fontSize: 18 }}
                      >
                        {b.brand}
                      </Link>
                    ) : (
                      <span style={{ flex: 1, color: '#0A1428', fontFamily: 'Cormorant Garamond, serif', fontSize: 18 }}>{b.brand}</span>
                    )}
                    <span
                      style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, fontWeight: 700, color: '#0A1428' }}
                      title={t('landing.index_highlights.score_label')}
                    >
                      {b.score}
                    </span>
                  </div>
                ))}
              </div>

              <Link
                to={`/clasament/${board.slug}`}
                style={{
                  marginTop: 18, fontFamily: 'DM Mono, monospace', fontSize: 12,
                  letterSpacing: 1, color: '#8B6914', textDecoration: 'none', fontWeight: 600,
                }}
              >
                {t('landing.index_highlights.view_leaderboard', { industry: board.label })}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
